import { readDecodedHash } from "@/utils/url-hash.util";
const SCROLL_LOCK_FALLBACK_MS = 1200;
function headerOffset() {
    const header = document.querySelector("[data-component='Header'], header");
    return header?.offsetHeight ?? 0;
}
function sectionId(link) {
    const href = link.getAttribute("href") ?? "";
    return href.startsWith("#") ? href.slice(1) : "";
}
export const PdpSectionNavigation = (element) => {
    const links = Array.from(element.querySelectorAll("a[href^='#']"));
    const details = element.querySelector("details");
    const currentSlot = element.querySelector("[data-pdp-nav-current]");
    const syncCurrentLabel = (id) => {
        if (!currentSlot) {
            return;
        }
        const source = links.find((link) => sectionId(link) === id && link.closest("[data-pdp-nav-menu]"));
        if (!source) {
            return;
        }
        currentSlot.replaceChildren(...Array.from(source.children, (node) => node.cloneNode(true)));
    };
    const setCurrent = (id) => {
        links.forEach((link) => {
            if (sectionId(link) === id) {
                link.setAttribute("aria-current", "true");
            }
            else {
                link.removeAttribute("aria-current");
            }
        });
        syncCurrentLabel(id);
    };
    // While a click-initiated scroll is in flight the observer would flip the current section for every
    // section we pass on the way, so ignore it until the scroll settles (scrollend, with a timeout fallback).
    let scrollInFlight = false;
    let scrollLockTimer;
    const releaseScrollLock = () => {
        scrollInFlight = false;
        window.clearTimeout(scrollLockTimer);
        window.removeEventListener("scrollend", releaseScrollLock);
    };
    const lockObserverUntilScrollEnd = () => {
        releaseScrollLock();
        scrollInFlight = true;
        window.addEventListener("scrollend", releaseScrollLock, { once: true });
        scrollLockTimer = window.setTimeout(releaseScrollLock, SCROLL_LOCK_FALLBACK_MS);
    };
    const onClick = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }
        const link = target.closest("a[href^='#']");
        if (!link || !element.contains(link)) {
            return;
        }
        const id = sectionId(link);
        if (!id) {
            return;
        }
        const section = document.getElementById(id);
        if (!section) {
            return;
        }
        event.preventDefault();
        details?.removeAttribute("open");
        const top = section.getBoundingClientRect().top + window.scrollY - headerOffset() - element.offsetHeight;
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        lockObserverUntilScrollEnd();
        window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? "auto" : "smooth" });
        history.pushState(null, "", `#${id}`);
        setCurrent(id);
        section.setAttribute("tabindex", "-1");
        section.focus({ preventScroll: true });
    };
    const closeMenu = (event) => {
        if (!details?.open) {
            return;
        }
        if (event.target instanceof Node && element.contains(event.target)) {
            return;
        }
        details.removeAttribute("open");
    };
    const onKeyDown = (event) => {
        if (event.key !== "Escape" || !details?.open) {
            return;
        }
        // Closing the menu removes the focused link from the layout, so hand focus back to the toggle.
        const focusWasInside = details.contains(document.activeElement);
        details.removeAttribute("open");
        if (focusWasInside) {
            details.querySelector("summary")?.focus();
        }
    };
    element.addEventListener("click", onClick);
    document.addEventListener("click", closeMenu);
    document.addEventListener("keydown", onKeyDown);
    const hashId = readDecodedHash();
    if (hashId && links.some((link) => sectionId(link) === hashId)) {
        setCurrent(hashId);
    }
    const seen = new Set();
    const sections = links
        .map((link) => sectionId(link))
        .filter((id) => {
        if (!id || seen.has(id)) {
            return false;
        }
        seen.add(id);
        return true;
    })
        .map((id) => document.getElementById(id))
        .filter((section) => section instanceof HTMLElement);
    let observer;
    if (sections.length > 0 && "IntersectionObserver" in window) {
        observer = new IntersectionObserver((entries) => {
            if (scrollInFlight) {
                return;
            }
            const visible = entries
                .filter((entry) => entry.isIntersecting)
                .sort((left, right) => right.intersectionRatio - left.intersectionRatio)[0];
            if (visible?.target instanceof HTMLElement && visible.target.id) {
                setCurrent(visible.target.id);
            }
        }, { rootMargin: "-30% 0px -55% 0px", threshold: [0.1, 0.5] });
        sections.forEach((section) => observer?.observe(section));
    }
    return () => {
        element.removeEventListener("click", onClick);
        document.removeEventListener("click", closeMenu);
        document.removeEventListener("keydown", onKeyDown);
        releaseScrollLock();
        observer?.disconnect();
    };
};
